import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

const API_URL = getContractApiUrl();

const COLUMN_ORDER = [
    'id',
    'contract_id',
    'property_address',
    'milestone',
    'status',
    'updated_at',
    'created_at'
];

function DeveloperMilestones() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filters
    const [contractId, setContractId] = useState('');
    const [propertyAddress, setPropertyAddress] = useState('');
    const [milestone, setMilestone] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const fetchMilestones = async () => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const params = { user_email: user.email };
            if (contractId) params.contract_id = contractId;
            if (propertyAddress) params.property_address = propertyAddress;
            if (milestone) params.milestone = milestone;
            if (dateStart) params.date_start = dateStart;
            if (dateEnd) params.date_end = dateEnd;

            const response = await axios.get(`${API_URL}/milestones`, { params });
            setMilestones(response.data.milestones || []);
        } catch (e) {
            setError(e.message || 'Failed to load milestones');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchMilestones();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser]);

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }

    if (!isKeyFloUser) return null;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Milestones (SQL contract_status)</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
                    <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Property Address</label>
                    <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="Address..." style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Milestone</label>
                    <input value={milestone} onChange={(e) => setMilestone(e.target.value)} placeholder="Milestone..." style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Updated Date</label>
                    <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Updated Date</label>
                    <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={fetchMilestones}>Apply Filters</button>
                <button onClick={() => { setContractId(''); setPropertyAddress(''); setMilestone(''); setDateStart(''); setDateEnd(''); fetchMilestones(); }}>Clear</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            {COLUMN_ORDER.map((col) => (
                                <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', whiteSpace: 'nowrap' }}>
                                    {col.replace(/_/g, ' ').toUpperCase()}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {milestones.map((ms) => (
                            <tr key={ms.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                {COLUMN_ORDER.map((col) => {
                                    let val = ms[col];
                                    if (col === 'updated_at' || col === 'created_at') {
                                        val = formatDateTime(val);
                                    }
                                    return (
                                        <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem' }}>
                                            {String(val || '')}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {milestones.length === 0 && !loading && (
                            <tr>
                                <td colSpan={COLUMN_ORDER.length} style={{ padding: '12px', textAlign: 'center' }}>No milestones found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default DeveloperMilestones;
